// # Tasks Component for Step 2 Mentorship
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './task_stats2';
import './tasks2.css'; // # Import tasks2 CSS

// # Import step icons
import stepTwoIcon from '../../../aneo_media/mentee/two.png';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import earIcon from '../../../general/library/step1/step1_icons/ear.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import emergencyIcon from '../../../general/library/step1/step1_icons/emergency.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import healthcareIcon from '../../../general/library/step1/step1_icons/healthcare.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../../../general/library/step1/step1_icons/newborn.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import qualityIcon from '../../../general/library/step1/step1_icons/quality.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import statisticsIcon from '../../../general/library/step1/step1_icons/statistics.png';
import surgeryIcon from '../../../general/library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../../../general/library/step1/step1_icons/well_patient.png';

function Tasks2({ isAdmin, isMentorView }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [chapterProgress, setChapterProgress] = useState({});
  
  // # Create base path based on admin status
  const basePath = isAdmin ? '/admin/mentorship' : '/mentorship';

  // # Load chapter progress on component mount
  useEffect(() => {
    const loadChapterProgress = async () => {
      if (!user?.uid) return;
      
      try {
        const db = getFirestore();
        // # Use db2_mentee for Step 2
        const menteeRef = doc(db, 'db2_mentee', user.uid);
        const menteeSnapshot = await getDoc(menteeRef);
        
        if (menteeSnapshot.exists()) {
          const menteeData = menteeSnapshot.data();
          const chapters = menteeData.chapters || {};
          
          // # Calculate progress for each chapter
          const progress = {};
          Object.entries(chapters).forEach(([chapterId, chapterData]) => {
            const tasks = chapterData.tasks || {};
            const totalTasks = Object.keys(tasks).length;
            // # Use 'done' property instead of 'status' to check completion
            const completedTasks = Object.values(tasks).filter(task => task.done).length;
            progress[chapterId] = {
              total: totalTasks,
              completed: completedTasks,
              percentage: totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0
            };
          });
          
          setChapterProgress(progress);
        }
      } catch (error) {
        console.error('Error loading chapter progress:', error);
      }
    };
    
    loadChapterProgress();
  }, [user?.uid]);

  // # Define chapters for Step 2 - EXACT match with master database
  const getChapters = () => {
    return [
      { id: 'Cardiovascular', title: 'Cardiovascular', icon: heartIcon },
      { id: 'Respiratory & ENT', title: 'Respiratory & ENT', icon: lungsIcon },
      { id: 'Renal & Urology', title: 'Renal & Urology', icon: kidneyIcon },
      { id: 'Endocrine', title: 'Endocrine', icon: endocrineIcon },
      { id: 'Hematology & Oncology', title: 'Hematology & Oncology', icon: bloodIcon },
      { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
      { id: 'Gastrointestinal', title: 'Gastrointestinal', icon: gitIcon },
      { id: 'Musculoskeletal', title: 'Musculoskeletal', icon: musculoskeletalIcon },
      { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
      { id: 'Ophthalmology', title: 'Ophthalmology', icon: eyeIcon },
      { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
      { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
      { id: 'Obstetrics & Gynecology', title: 'Obstetrics & Gynecology', icon: reproductiveIcon },
      { id: 'Pediatrics & Neonatology', title: 'Pediatrics & Neonatal Medicine', icon: newbornIcon },
      { id: 'Genetics', title: 'Genetics', icon: biochemistryIcon },
      { id: 'Infectious Disease', title: 'Infectious Disease', icon: infectiousIcon },
      { id: 'Surgery & Anesthesia', title: 'Surgery & Anesthesia', icon: surgeryIcon },
      { id: 'Emergency Medicine & Trauma', title: 'Emergency Medicine & Trauma', icon: emergencyIcon },
      { id: 'Well-Patient', title: 'Preventive Medicine', icon: wellPatientIcon },
      { id: 'Biostatistics & Epidemiology', title: 'Biostatistics & Epidemiology', icon: statisticsIcon },
      { id: 'Ethics & Communication Skills', title: 'Ethics & Communication Skills', icon: publicHealthIcon },
      { id: 'Healthcare Delivery', title: 'Healthcare Delivery', icon: healthcareIcon },
      { id: 'Quality & Safety', title: 'Quality & Safety', icon: qualityIcon },
      { id: 'Pharmacology Review', title: 'Pharmacology Review', icon: pharmaIcon }
    ];
  };

  // # Handle chapter navigation
  const handleChapterClick = (chapterId) => {
    // # Check if we're in mentor view with a mentee username
    const menteeUsername = sessionStorage.getItem('currentViewingMenteeUsername');
    
    if (isMentorView && menteeUsername) {
      // # Navigate to chapter with username in URL for mentor view
      navigate(`${basePath}/step2/${menteeUsername}/tutora2/chapter/${chapterId}`, { 
        state: { 
          isAdmin, 
          isMentorView 
        } 
      });
    } else {
      // # Standard navigation without username
      navigate(`${basePath}/step2/tutora2/chapter/${chapterId}`, { 
        state: { 
          isAdmin, 
          isMentorView 
        } 
      });
    }
  };

  return (
    <div className="tasks2_container">
      <SharedHeader />
      <div className="tasks2_main">
        <div className="tasks2_content">
          <div className="tasks2_header">
            <h1>USMLE Step 2 Tasks</h1>
          </div>

          <div className="tasks2_chapters_grid">
            {getChapters().map((chapter) => {
              const progress = chapterProgress[chapter.id] || { total: 0, completed: 0, percentage: 0 };
              
              return (
                <div 
                  key={chapter.id}
                  className="tasks2_chapter_card"
                  onClick={() => handleChapterClick(chapter.id)}
                >
                  <div className="tasks2_chapter_icon">
                    <img src={chapter.icon} alt={chapter.title} />
                  </div>
                  <h3>{chapter.title}</h3>
                  {progress.total > 0 && (
                    <div className="tasks2_chapter_progress">
                      <div 
                        className="tasks2_progress_bar"
                        style={{ width: `${progress.percentage}%` }}
                      />
                      <span className="tasks2_progress_text">
                        {progress.completed}/{progress.total} tasks
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
}

export default Tasks2;
